import { useState, useEffect, useMemo } from 'react';
import { MdTrendingUp, MdGroups, MdCheckCircle, MdSchedule, MdSentimentSatisfied, MdSentimentNeutral, MdSentimentDissatisfied, MdSentimentVerySatisfied, MdBarChart } from 'react-icons/md';
import Card from "components/card";
import BarChart from "components/charts/BarChart";
import { introStatsService } from '@services/introStatsService';
import { StatsData } from 'types/introStats.types';
import { useTenant } from '@contexts/TenantContext';

const IntroStats = () => {
  const { currentTenantId } = useTenant();
  const [statsData, setStatsData] = useState<StatsData | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStatsData(null);
        const data = await introStatsService.fetchIntroStats(currentTenantId);
        setStatsData(data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };

    if (currentTenantId) {
      fetchStats();
    }
  }, [currentTenantId]);

  const chartConfig = useMemo(() => {
    if (!statsData?.journeyFunnel || statsData.journeyFunnel.length === 0) {
      return {
        chartData: [
          {
            name: "FEEDBACK COLLECTED",
            data: []
          },
          {
            name: "CONNECTED",
            data: []
          },
          {
            name: "INTRODUCED",
            data: []
          }
        ],
        categories: []
      };
    }

    return introStatsService.transformJourneyFunnelForChart(statsData.journeyFunnel);
  }, [statsData?.journeyFunnel]);

  const calculateMonthDiff = (statsData: StatsData): string => {
    if (!statsData.totalIntrosLastMonth) {
      return 'No data for last month';
    }

    const diff = statsData.totalIntros - statsData.totalIntrosLastMonth;

    if (diff > 0) {
      return `+${diff} from last month`;
    } else if (diff < 0) {
      return `${diff} from last month`;
    } else {
      return 'No change from last month';
    }
  };

  // Get NPS sentiment icon and color based on score (industry standard thresholds)
  const getNpsSentiment = (npsScore: number) => {
    if (npsScore >= 70) {
      return { icon: MdSentimentVerySatisfied, color: 'text-green-500', label: 'Excellent' };
    } else if (npsScore >= 50) {
      return { icon: MdSentimentSatisfied, color: 'text-green-400', label: 'Great' };
    } else if (npsScore >= 30) {
      return { icon: MdSentimentSatisfied, color: 'text-lime-500', label: 'Good' };
    } else if (npsScore >= 0) {
      return { icon: MdSentimentNeutral, color: 'text-yellow-500', label: 'Needs Improvement' };
    } else {
      return { icon: MdSentimentDissatisfied, color: 'text-red-500', label: 'Critical' };
    }
  };

  if (!statsData) {
    return (
      <div className="mt-8 w-full">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500 mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading intro statistics...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8 w-full min-h-0">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/30 p-5 rounded-2xl border border-blue-200 dark:border-blue-800/50 hover:shadow-lg transition-all duration-300">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <MdGroups className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wider">Total Introductions</p>
              </div>
              <p className="text-3xl font-bold text-blue-900 dark:text-blue-100 mb-1">{statsData.totalIntros}</p>
              <p className="text-xs text-blue-600 dark:text-blue-400">{calculateMonthDiff(statsData)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/30 p-5 rounded-2xl border border-green-200 dark:border-green-800/50 hover:shadow-lg transition-all duration-300">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <MdCheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                <p className="text-xs font-semibold text-green-700 dark:text-green-300 uppercase tracking-wider">Success Rate</p>
              </div>
              <p className="text-3xl font-bold text-green-900 dark:text-green-100 mb-1">{statsData.successRate}%</p>
              <p className="text-xs text-green-600 dark:text-green-400">intros turned into connections</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/30 p-5 rounded-2xl border border-amber-200 dark:border-amber-800/50 hover:shadow-lg transition-all duration-300">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <MdSchedule className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wider">Pending</p>
              </div>
              <p className="text-3xl font-bold text-amber-900 dark:text-amber-100 mb-1">{statsData.pendingIntros}</p>
              <p className="text-xs text-amber-600 dark:text-amber-400">Scheduled for the 10th</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/30 p-5 rounded-2xl border border-purple-200 dark:border-purple-800/50 hover:shadow-lg transition-all duration-300">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <MdTrendingUp className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                <p className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wider">Avg Response Time</p>
              </div>
              <p className="text-3xl font-bold text-purple-900 dark:text-purple-100">{statsData.averageResponseTime}</p>
              <p className="text-xs text-purple-600 dark:text-purple-400">Response auto-handled by Latte AI</p>
            </div>
          </div>
        </div>
      </div>

      {/* Introduction Journey Funnel and Net Promoter Score Side by Side */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-8">
        {/* Introduction Journey Funnel */}
        <Card extra="flex flex-col bg-white w-full rounded-3xl py-6 px-2 text-center dark:!bg-navy-800">
          <div className="mb-auto flex items-center justify-between px-6">
            <h2 className="text-lg font-bold text-navy-700 dark:text-white">
              Introduction Journey Funnel
            </h2>
            <button className="!linear z-[1] flex items-center justify-center rounded-lg bg-lightPrimary p-2 text-brand-500 !transition !duration-200 hover:bg-gray-100 active:bg-gray-200 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10">
              <MdBarChart className="h-6 w-6" />
            </button>
          </div>

          <div className="md:mt-16 lg:mt-0">
            <div className="h-[250px] w-full xl:h-[350px]">
              {chartConfig.chartData[0].data.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <p className="text-gray-400 dark:text-gray-500 text-sm">No journey funnel data available</p>
                  </div>
                </div>
              ) : (
                <BarChart
                  chartData={chartConfig.chartData}
                  chartOptions={{
                  chart: {
                    type: 'bar',
                    stacked: true,
                    toolbar: { show: false }
                  },
                  plotOptions: {
                    bar: {
                      horizontal: false,
                      columnWidth: '20px',
                      borderRadius: 0,
                      distributed: false
                    }
                  },
                  colors: ["#5E37FF", "#6AD2FF", "#E1E9F8"],
                  dataLabels: { enabled: false },
                  stroke: {
                    show: false
                  },
                  xaxis: {
                    categories: chartConfig.categories,
                    show: false,
                    labels: {
                      show: true,
                      style: { colors: "#A3AED0", fontSize: "14px", fontWeight: "500" }
                    },
                    axisBorder: { show: false },
                    axisTicks: { show: false }
                  },
                  yaxis: {
                    show: false,
                    labels: { show: false }
                  },
                  grid: {
                    show: true,
                    borderColor: "rgba(163, 174, 208, 0.3)",
                    yaxis: { lines: { show: false } },
                    xaxis: { lines: { show: false } }
                  },
                  fill: {
                    opacity: 1
                  },
                  tooltip: {
                    y: {
                      formatter: function (val: number) {
                        return val + " introductions"
                      }
                    },
                    style: { fontSize: "12px" },
                    theme: "dark"
                  },
                  legend: { show: false }
                }}
              />
              )}
            </div>
          </div>
        </Card>

        {/* Net Promoter Score */}
        <Card extra="flex flex-col bg-white w-full rounded-3xl py-6 px-2 text-center dark:!bg-navy-800">
          <div className="mb-auto flex items-center justify-between px-6">
            <h2 className="text-lg font-bold text-navy-700 dark:text-white">
              Net Promoter Score
            </h2>
            {(() => {
              const sentiment = getNpsSentiment(statsData.npsScore);
              const SentimentIcon = sentiment.icon;
              return (
                <div
                  className={`!linear z-[1] flex items-center justify-center rounded-lg bg-lightPrimary p-2 ${sentiment.color} !transition !duration-200 dark:bg-navy-700`}
                  title={sentiment.label}
                >
                  <SentimentIcon className="h-6 w-6" />
                </div>
              );
            })()}
          </div>

          <div className="md:mt-16 lg:mt-0">
            <div className="h-[250px] w-full xl:h-[350px] flex flex-col items-center justify-center px-8">
              {/* Large NPS Score Display */}
              <div className="text-center mb-8">
                <div className="relative inline-block">
                  <div className="text-6xl font-bold text-navy-700 dark:text-white mb-2">
                    {statsData.npsScore}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                    NPS Score
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Based on {statsData.npsResponses} responses
                </p>
              </div>

              {/* Horizontal Stacked Bar */}
              <div className="w-full max-w-sm">
                <div className="flex w-full h-6 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 mb-4">
                  <div
                    className="flex items-center justify-center text-xs font-medium text-white"
                    style={{
                      width: `${statsData.promoterPercentage}%`,
                      backgroundColor: '#5E37FF'
                    }}
                    title={`Promoters: ${statsData.promoterPercentage}%`}
                  >
                    {statsData.promoterPercentage > 15 ? `${statsData.promoterPercentage}%` : ''}
                  </div>
                  <div
                    className="flex items-center justify-center text-xs font-medium text-white"
                    style={{
                      width: `${100 - statsData.promoterPercentage - statsData.detractorPercentage}%`,
                      backgroundColor: '#6AD2FF'
                    }}
                    title={`Passives: ${100 - statsData.promoterPercentage - statsData.detractorPercentage}%`}
                  >
                    {(100 - statsData.promoterPercentage - statsData.detractorPercentage) > 15 ? `${100 - statsData.promoterPercentage - statsData.detractorPercentage}%` : ''}
                  </div>
                  <div
                    className="flex items-center justify-center text-xs font-medium text-white"
                    style={{
                      width: `${statsData.detractorPercentage}%`,
                      backgroundColor: '#E1E9F8'
                    }}
                    title={`Detractors: ${statsData.detractorPercentage}%`}
                  >
                    {statsData.detractorPercentage > 8 ? `${statsData.detractorPercentage}%` : ''}
                  </div>
                </div>

                {/* Legend */}
                <div className="flex justify-center items-center gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#5E37FF' }}></div>
                    <span className="text-gray-600 dark:text-gray-400">Promoters</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#6AD2FF' }}></div>
                    <span className="text-gray-600 dark:text-gray-400">Passives</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#E1E9F8' }}></div>
                    <span className="text-gray-600 dark:text-gray-400">Detractors</span>
                  </div>
                </div>

                {/* Explanation */}
                <div className="mt-4 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="text-xs text-blue-800 dark:text-blue-300 text-center">
                    <div className="font-medium mb-1">NPS 52 = 64% Promoters - 12% Detractors</div>
                    <div className="text-xs opacity-75 mb-1">
                      Score calculated as: % Promoters minus % Detractors
                    </div>
                    <div className="text-xs opacity-75">
                      Promoters (9-10) actively refer others • Passives (7-8) satisfied but neutral • Detractors (0-6) need attention
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

    </div>
  );
};

export default IntroStats;